// chats-layer/stores/chats.ui-store.ts
import { ref, computed } from 'vue'

import { defineStore } from 'pinia'

import type { ChatPanelState, ChatPanelView, IActiveChatWindow, IConversation } from '../types/conversation.types'

export const useChatsUIStore = defineStore('chats-ui', () => {
  // ===== STATE =====

  // Panel state
  const panelState = ref<ChatPanelState>('closed')

  // Panel view (conversations list vs new chat)
  const panelView = ref<ChatPanelView>('conversations')

  // Search query for new chat member search
  const newChatSearchQuery = ref('')

  // Currently selected conversation (for viewing in panel)
  const selectedConversationId = ref<string | null>(null)

  // Active chat windows (for multi-window mode)
  const activeChatWindows = ref<IActiveChatWindow[]>([])

  // Maximum number of chat windows that can be open
  const maxChatWindows = 3

  // Last create-conversation failure, awaiting a toast.
  //
  // The chats data layer is reachable from the app shell, which renders the notification provider
  // in its own template — so it cannot resolve `useNotificationHelper` and must not try. It records
  // failures here instead; `ChatErrorToaster`, mounted inside the provider, renders them. The
  // counter makes two identical consecutive failures distinct so the watcher fires for both.
  const lastCreateConversationError = ref<{ error: unknown; seq: number } | null>(null)
  let createConversationErrorSeq = 0

  // ===== COMPUTED =====

  const isPanelOpen = computed(() => panelState.value === 'open' || panelState.value === 'expanded')
  const isPanelMinimized = computed(() => panelState.value === 'minimized')
  const isPanelExpanded = computed(() => panelState.value === 'expanded')
  const isNewChatView = computed(() => panelView.value === 'newChat')

  // ===== ACTIONS =====

  /**
   * Toggle the chat panel open/closed
   */
  const togglePanel = () => {
    if (panelState.value === 'closed' || panelState.value === 'minimized') {
      panelState.value = 'open'
    } else {
      panelState.value = 'closed'
    }
  }

  /**
   * Open the chat panel
   */
  const openPanel = () => {
    panelState.value = 'open'
  }

  /**
   * Close the chat panel
   */
  const closePanel = () => {
    panelState.value = 'closed'
    selectedConversationId.value = null
  }

  /**
   * Minimize the chat panel
   */
  const minimizePanel = () => {
    panelState.value = 'minimized'
  }

  /**
   * Expand the chat panel to full size
   */
  const expandPanel = () => {
    panelState.value = 'expanded'
  }

  /**
   * Set the new chat search query
   */
  const setNewChatSearchQuery = (query: string) => {
    newChatSearchQuery.value = query
  }

  /**
   * Select a conversation to view
   */
  const selectConversation = (conversationId: string | null) => {
    selectedConversationId.value = conversationId
  }

  /**
   * Switch to new chat view
   */
  const showNewChatView = () => {
    panelView.value = 'newChat'
    newChatSearchQuery.value = ''
  }

  /**
   * Switch back to conversations list view
   */
  const showConversationsView = () => {
    panelView.value = 'conversations'
    newChatSearchQuery.value = ''
  }

  /**
   * Open a chat window for a conversation
   */
  const openChatWindow = (conversation: IConversation) => {
    // Check if already open
    const existingIndex = activeChatWindows.value.findIndex(w => w.conversationId === conversation._id)

    if (existingIndex !== -1) {
      // Bring to front (highest position)
      const window = activeChatWindows.value[existingIndex]
      window.isMinimized = false
      return
    }

    // If max windows reached, remove the oldest one
    if (activeChatWindows.value.length >= maxChatWindows) {
      activeChatWindows.value.shift()
    }

    // Add new window
    activeChatWindows.value.push({
      conversationId: conversation._id,
      conversation,
      isMinimized: false,
      position: activeChatWindows.value.length,
    })
  }

  /**
   * Close a chat window
   */
  const closeChatWindow = (conversationId: string) => {
    const index = activeChatWindows.value.findIndex(w => w.conversationId === conversationId)
    if (index !== -1) {
      activeChatWindows.value.splice(index, 1)
      // Recalculate positions
      activeChatWindows.value.forEach((w, i) => {
        w.position = i
      })
    }
  }

  /**
   * Toggle minimize state of a chat window
   */
  const toggleChatWindowMinimize = (conversationId: string) => {
    const window = activeChatWindows.value.find(w => w.conversationId === conversationId)
    if (window) {
      window.isMinimized = !window.isMinimized
    }
  }

  /**
   * Reset the store state
   */
  const reset = () => {
    panelState.value = 'closed'
    panelView.value = 'conversations'
    newChatSearchQuery.value = ''
    selectedConversationId.value = null
    activeChatWindows.value = []
    lastCreateConversationError.value = null
    createConversationErrorSeq = 0
  }

  /**
   * Record a create-conversation failure for the global toaster to surface.
   */
  const reportCreateConversationError = (error: unknown) => {
    createConversationErrorSeq += 1
    lastCreateConversationError.value = { error, seq: createConversationErrorSeq }
  }

  /**
   * Clear the pending failure once it has been surfaced.
   */
  const clearCreateConversationError = () => {
    lastCreateConversationError.value = null
  }

  return {
    // State (exposed as computed for reactivity)
    panelState: computed(() => panelState.value),
    panelView: computed(() => panelView.value),
    newChatSearchQuery: computed(() => newChatSearchQuery.value),
    selectedConversationId: computed(() => selectedConversationId.value),
    activeChatWindows: computed(() => activeChatWindows.value),
    lastCreateConversationError: computed(() => lastCreateConversationError.value),
    maxChatWindows,

    // Computed
    isPanelOpen,
    isPanelMinimized,
    isPanelExpanded,
    isNewChatView,

    // Actions
    togglePanel,
    openPanel,
    closePanel,
    minimizePanel,
    expandPanel,
    setNewChatSearchQuery,
    selectConversation,
    showNewChatView,
    showConversationsView,
    openChatWindow,
    closeChatWindow,
    toggleChatWindowMinimize,
    reportCreateConversationError,
    clearCreateConversationError,
    reset,
  }
})
